"use client";

import {
	ArrowRight,
	CheckCircle,
	Clock,
	Code,
	Copy,
	Loader2,
	Sparkles,
} from "lucide-react";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";

import { toast } from "@/components/ui/use-toast";

import { Button, GlassContainer, TabButton } from "@meetploy/storybook";

const AUTO_CYCLE_INTERVAL = 5000;

type TabKey =
	| "workers"
	| "static"
	| "db"
	| "queue"
	| "workflows"
	| "cron"
	| "ai";

const codeSnippets: Record<TabKey, string> = {
	workers: `export default {
  async fetch(request, env) {
    return Response.json({
      message: "Hello from Ploy!"
    });
  },
};`,
	static: `<!DOCTYPE html>
<html>
  <head>
    <title>My Static Site</title>
  </head>
  <body>
    <h1>Hello World!</h1>
    <p>Welcome to my static site.</p>
  </body>
</html>`,
	db: `export default {
  async fetch(request, env) {
    const { results } = await env.DB
      .prepare("SELECT * FROM users")
      .all();

    return Response.json({ users: results });
  },
};`,
	queue: `export default {
  async queue(batch, env) {
    for (const message of batch.messages) {
      await processTask(message.body);
    }
  },
};

async function processTask(task) {
  // Process queue task
  console.log('Processing:', task);
}`,
	workflows: `export default {
  async fetch(request, env) {
    const { executionId } = await env.ONBOARDING
      .trigger({ userId: "123" });
    return Response.json({ executionId });
  },

  workflows: {
    onboarding: {
      handler: async ({ input, step }) => {
        await step.run("welcome", () => sendWelcome(input));
        await step.run("notify-team", () => notify(input));
      },
    },
  },
};`,
	cron: `# ploy.yaml
cron:
  HOURLY_CLEANUP: "0 * * * *"
  DAILY_REPORT: "0 9 * * *"

# src/index.ts
export default {
  async scheduled(event, env) {
    if (event.cron === "0 9 * * *") {
      await env.TASKS.send({ job: "daily-report" });
    }
  },
};`,
	ai: `# ploy.yaml
ai: true

# src/index.ts
export default {
  async fetch(request, env) {
    const { prompt } = await request.json();
    const result = await env.AI.run("glm-4.6v-flash", {
      prompt,
    });
    return Response.json(result);
  },
};`,
};

const tabs: { key: TabKey; label: string; href: string }[] = [
	{ key: "workers", label: "Workers", href: "/features/workers" },
	{ key: "static", label: "Static", href: "/features/static-sites" },
	{ key: "db", label: "DB", href: "/features/database" },
	{ key: "queue", label: "Queue", href: "/features/queues" },
	{ key: "workflows", label: "Workflows", href: "/features/workflows" },
	{ key: "cron", label: "Cron", href: "/features/cron" },
	{ key: "ai", label: "AI", href: "/features/ai" },
];

function WorkersPreview() {
	return (
		<div className="p-6">
			<div className="mb-6">
				<div className="inline-flex items-center gap-2 px-2 py-1 rounded bg-green-500/20 border border-green-500/30 text-green-400 text-xs mb-4">
					GET
				</div>
				<span className="text-gray-400 ml-2">/api/hello</span>
			</div>

			<div className="mb-4">
				<div className="text-gray-500 text-xs mb-2">Response</div>
				<div className="p-4 rounded-lg bg-white/[0.02] dark:bg-white/[0.02] border border-gray-200 dark:border-white/5">
					<pre className="text-gray-700 dark:text-gray-300 font-mono text-sm">
						{`{
  "message": "Hello from Ploy!"
}`}
					</pre>
				</div>
			</div>

			<div className="flex items-center gap-3 text-sm">
				<CheckCircle className="w-4 h-4 text-green-500" />
				<span className="text-green-600 dark:text-green-400">200 OK</span>
				<span className="text-gray-400">•</span>
				<span className="text-gray-500">12ms</span>
			</div>
		</div>
	);
}

function StaticPreview() {
	return (
		<div className="p-6">
			<div className="mb-6 p-4 rounded-lg bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/5">
				<div className="flex items-center gap-3 mb-4">
					<div className="flex gap-1.5">
						<div className="w-3 h-3 rounded-full bg-red-500" />
						<div className="w-3 h-3 rounded-full bg-yellow-500" />
						<div className="w-3 h-3 rounded-full bg-green-500" />
					</div>
					<div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/5 text-xs text-gray-500 dark:text-gray-400">
						<svg
							className="w-3 h-3"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
							/>
						</svg>
						my-site.ploy.app
					</div>
				</div>

				<div className="text-center py-12">
					<h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
						Hello World!
					</h2>
					<p className="text-gray-500 dark:text-gray-400 text-lg">
						Welcome to my static site.
					</p>
				</div>
			</div>
		</div>
	);
}

function DbPreview() {
	return (
		<>
			{/* Query Bar */}
			<div className="p-4 border-b border-gray-200 dark:border-white/10 flex items-center gap-2 text-gray-500 dark:text-gray-400 font-mono text-sm">
				<span className="text-purple-600 dark:text-purple-400 font-bold">
					SELECT
				</span>
				<span className="text-gray-900 dark:text-white">*</span>
				<span className="text-purple-600 dark:text-purple-400 font-bold">
					FROM
				</span>
				<span className="text-gray-900 dark:text-white">users</span>
			</div>

			{/* Results Table */}
			<div className="w-full overflow-x-auto">
				<table className="w-full text-left border-collapse font-mono text-sm">
					<thead>
						<tr className="bg-gray-50 dark:bg-white/[0.04] text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider">
							<th className="p-3 font-medium border-b border-gray-200 dark:border-white/10 w-16">
								id
							</th>
							<th className="p-3 font-medium border-b border-gray-200 dark:border-white/10">
								name
							</th>
							<th className="p-3 font-medium border-b border-gray-200 dark:border-white/10">
								email
							</th>
						</tr>
					</thead>
					<tbody className="text-gray-700 dark:text-gray-300">
						<tr className="border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
							<td className="p-3 text-gray-500">1</td>
							<td className="p-3">Alice</td>
							<td className="p-3 text-gray-500 dark:text-gray-400">
								alice@example.com
							</td>
						</tr>
						<tr className="border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
							<td className="p-3 text-gray-500">2</td>
							<td className="p-3">Bob</td>
							<td className="p-3 text-gray-500 dark:text-gray-400">
								bob@example.com
							</td>
						</tr>
						<tr className="hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
							<td className="p-3 text-gray-500">3</td>
							<td className="p-3">Carol</td>
							<td className="p-3 text-gray-500 dark:text-gray-400">
								carol@example.com
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			{/* Footer Info */}
			<div className="p-3 text-xs text-gray-500 dark:text-gray-500 border-t border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.02]">
				3 rows returned
			</div>
		</>
	);
}

function QueuePreview() {
	return (
		<div className="p-6">
			<h3 className="text-gray-500 dark:text-gray-400 text-sm mb-4">
				Task Queue
			</h3>

			<div className="space-y-3">
				{/* Completed Task */}
				<div className="p-4 rounded-lg bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/5 flex items-center justify-between">
					<div className="flex items-center gap-3">
						<CheckCircle className="w-5 h-5 text-green-500" />
						<span className="text-gray-700 dark:text-gray-300">send-email</span>
					</div>
					<span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-600 dark:text-green-400 border border-green-500/30">
						completed
					</span>
				</div>

				{/* Processing Task */}
				<div className="p-4 rounded-lg bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/5 flex items-center justify-between">
					<div className="flex items-center gap-3">
						<Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
						<span className="text-gray-700 dark:text-gray-300">
							process-image
						</span>
					</div>
					<span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-500/30">
						processing
					</span>
				</div>

				{/* Pending Task */}
				<div className="p-4 rounded-lg bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/5 flex items-center justify-between">
					<div className="flex items-center gap-3">
						<Clock className="w-5 h-5 text-gray-400 dark:text-gray-500" />
						<span className="text-gray-700 dark:text-gray-300">
							generate-report
						</span>
					</div>
					<span className="px-2 py-1 rounded text-xs bg-gray-500/20 text-gray-600 dark:text-gray-400 border border-gray-500/30">
						pending
					</span>
				</div>
			</div>
		</div>
	);
}

function WorkflowsPreview() {
	return (
		<div className="p-6">
			<div className="flex items-center justify-between mb-6">
				<div className="text-gray-500 dark:text-gray-400 text-sm">
					Workflow:{" "}
					<span className="text-gray-900 dark:text-white">onboarding</span>
				</div>
				<span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-500/30">
					Running
				</span>
			</div>

			<div className="space-y-6 relative">
				{/* Vertical line */}
				<div className="absolute left-[13px] top-8 bottom-8 w-px bg-gray-200 dark:bg-white/10" />

				{/* Step 1 - Completed */}
				<div className="flex items-start gap-4 relative">
					<div className="w-7 h-7 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center flex-shrink-0 relative z-10">
						<CheckCircle className="w-4 h-4 text-green-500" />
					</div>
					<div className="flex-1 pt-0.5">
						<div className="text-gray-700 dark:text-gray-300 mb-1">welcome</div>
						<div className="text-xs text-gray-500">120ms</div>
					</div>
				</div>

				{/* Step 2 - Completed */}
				<div className="flex items-start gap-4 relative">
					<div className="w-7 h-7 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center flex-shrink-0 relative z-10">
						<CheckCircle className="w-4 h-4 text-green-500" />
					</div>
					<div className="flex-1 pt-0.5">
						<div className="text-gray-700 dark:text-gray-300 mb-1">
							send-email
						</div>
						<div className="text-xs text-gray-500">340ms</div>
					</div>
				</div>

				{/* Step 3 - Running */}
				<div className="flex items-start gap-4 relative">
					<div className="w-7 h-7 rounded-full bg-blue-500/20 border-2 border-blue-500 flex items-center justify-center flex-shrink-0 relative z-10">
						<Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
					</div>
					<div className="flex-1 pt-0.5">
						<div className="text-gray-700 dark:text-gray-300 mb-1">
							notify-team
						</div>
						<div className="text-xs text-gray-500">...</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function CronPreview() {
	const runs = [
		{ name: "HOURLY_CLEANUP", cron: "0 * * * *", when: "14 min ago" },
		{ name: "DAILY_REPORT", cron: "0 9 * * *", when: "3 h ago" },
		{ name: "EVERY_10_SECONDS", cron: "*/10 * * * * *", when: "8 s ago" },
	];

	return (
		<div className="p-6">
			<h3 className="text-gray-500 dark:text-gray-400 text-sm mb-4">
				Scheduled Runs
			</h3>

			<div className="space-y-3">
				{runs.map((run) => (
					<div
						key={run.name}
						className="p-4 rounded-lg bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/5 flex items-center justify-between"
					>
						<div className="flex items-center gap-3">
							<Clock className="w-5 h-5 text-teal-500" />
							<div>
								<div className="text-gray-700 dark:text-gray-300">
									{run.name}
								</div>
								<div className="text-xs text-gray-500">{run.cron}</div>
							</div>
						</div>
						<div className="flex items-center gap-2 text-xs">
							<CheckCircle className="w-4 h-4 text-green-500" />
							<span className="text-gray-500">{run.when}</span>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

function AiPreview() {
	return (
		<div className="p-6">
			<div className="mb-6">
				<div className="inline-flex items-center gap-2 px-2 py-1 rounded bg-cyan-500/20 border border-cyan-500/30 text-cyan-600 dark:text-cyan-400 text-xs mb-4">
					<Sparkles className="w-3 h-3" />
					env.AI.run
				</div>
				<span className="text-gray-400 ml-2">glm-4.6v-flash</span>
			</div>

			<div className="mb-4">
				<div className="text-gray-500 text-xs mb-2">Prompt</div>
				<div className="p-4 rounded-lg bg-white/[0.02] dark:bg-white/[0.02] border border-gray-200 dark:border-white/5 text-gray-700 dark:text-gray-300">
					Summarize this support ticket in one sentence.
				</div>
			</div>

			<div className="mb-4">
				<div className="text-gray-500 text-xs mb-2">Response</div>
				<div className="p-4 rounded-lg bg-white/[0.02] dark:bg-white/[0.02] border border-gray-200 dark:border-white/5 text-gray-700 dark:text-gray-300">
					Customer cannot reset their password because the email link expires
					before it arrives.
				</div>
			</div>

			<div className="flex items-center gap-3 text-sm">
				<CheckCircle className="w-4 h-4 text-green-500" />
				<span className="text-green-600 dark:text-green-400">
					No provider keys
				</span>
				<span className="text-gray-400">•</span>
				<span className="text-gray-500">token scoped to deployment</span>
			</div>
		</div>
	);
}

function CodeView({ code }: { code: string }) {
	const copyToClipboard = async () => {
		try {
			await navigator.clipboard.writeText(code);
			toast({
				title: "Copied to clipboard",
				description: "Code snippet has been copied.",
				duration: 3000,
			});
		} catch (err) {
			console.error("Failed to copy text: ", err);
		}
	};

	return (
		<div className="relative">
			<div className="absolute top-4 right-4 z-10">
				<Button
					variant="outline"
					size="sm"
					className="text-xs"
					onClick={() => void copyToClipboard()}
				>
					<Copy className="w-3 h-3 mr-1.5" />
					Copy
				</Button>
			</div>
			<pre className="p-6 text-sm overflow-x-auto bg-gray-50 dark:bg-[#1a1f2e] text-gray-700 dark:text-gray-300 leading-relaxed font-mono">
				<code>{code}</code>
			</pre>
		</div>
	);
}

interface FeatureConsoleProps {
	autoLoop?: boolean;
}

export function FeatureConsole({ autoLoop = false }: FeatureConsoleProps) {
	const [activeIndex, setActiveIndex] = useState(0);
	const [showCode, setShowCode] = useState(false);
	const [progress, setProgress] = useState(0);
	const progressRef = useRef<number>(0);
	const animationFrameRef = useRef<number>(0);
	const lastTimeRef = useRef<number>(0);
	const containerRef = useRef<HTMLDivElement>(null);
	const isPausedRef = useRef(false);

	const activeTab = tabs[activeIndex].key;

	useEffect(() => {
		if (!autoLoop) {
			return;
		}

		const container = containerRef.current;
		if (!container) {
			return;
		}

		const handlePointerEnter = () => {
			isPausedRef.current = true;
		};
		const handlePointerLeave = () => {
			isPausedRef.current = false;
		};

		container.addEventListener("pointerenter", handlePointerEnter);
		container.addEventListener("pointerleave", handlePointerLeave);

		return () => {
			container.removeEventListener("pointerenter", handlePointerEnter);
			container.removeEventListener("pointerleave", handlePointerLeave);
		};
	}, [autoLoop]);

	const resetProgress = useCallback(() => {
		progressRef.current = 0;
		setProgress(0);
		lastTimeRef.current = 0;
	}, []);

	useEffect(() => {
		if (!autoLoop) {
			return;
		}

		const animate = (timestamp: number) => {
			if (isPausedRef.current) {
				lastTimeRef.current = 0;
				animationFrameRef.current = requestAnimationFrame(animate);
				return;
			}

			if (!lastTimeRef.current) {
				lastTimeRef.current = timestamp;
			}

			const deltaTime = timestamp - lastTimeRef.current;
			lastTimeRef.current = timestamp;

			progressRef.current += deltaTime / AUTO_CYCLE_INTERVAL;
			setProgress(Math.min(progressRef.current, 1));

			if (progressRef.current >= 1) {
				setActiveIndex((prev) => (prev + 1) % tabs.length);
				resetProgress();
			}

			animationFrameRef.current = requestAnimationFrame(animate);
		};

		animationFrameRef.current = requestAnimationFrame(animate);

		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}
		};
	}, [autoLoop, resetProgress]);

	const handleTabClick = (index: number) => {
		setActiveIndex(index);
		resetProgress();
	};

	const renderPreview = () => {
		switch (activeTab) {
			case "workers":
				return <WorkersPreview />;
			case "static":
				return <StaticPreview />;
			case "db":
				return <DbPreview />;
			case "queue":
				return <QueuePreview />;
			case "workflows":
				return <WorkflowsPreview />;
			case "cron":
				return <CronPreview />;
			case "ai":
				return <AiPreview />;
		}
	};

	return (
		<div ref={containerRef}>
			<GlassContainer
				rounded="2xl"
				shadow="lg"
				className="w-full max-w-4xl mx-auto overflow-hidden !p-0"
			>
				{/* Tabs Header */}
				<div className="flex items-center px-2 border-b border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-white/[0.02]">
					{tabs.map((tab, index) => (
						<div key={tab.key} className="relative">
							<TabButton
								active={index === activeIndex}
								onClick={() => {
									handleTabClick(index);
								}}
							>
								{tab.label}
							</TabButton>
							{autoLoop && index === activeIndex && (
								<span
									className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-transparent via-gray-400 dark:via-gray-500 to-transparent transition-all duration-75"
									style={{ width: `${progress * 100}%` }}
								/>
							)}
						</div>
					))}
				</div>

				{/* Console Content */}
				<div className="font-mono text-sm min-h-[350px]">
					{showCode ? (
						<CodeView code={codeSnippets[activeTab]} />
					) : (
						renderPreview()
					)}
				</div>

				{/* Action Footer */}
				<div className="p-3 border-t border-gray-200 dark:border-white/10 flex items-center justify-between bg-gray-50/50 dark:bg-white/[0.02]">
					<Button
						variant="ghost"
						size="sm"
						className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white group"
						render={<Link prefetch={true} href={tabs[activeIndex].href} />}
					>
						Learn more{" "}
						<ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
					</Button>
					<Button
						variant={showCode ? "default" : "outline"}
						size="sm"
						onClick={() => {
							setShowCode(!showCode);
						}}
					>
						<Code className="w-4 h-4 mr-1.5" /> Code
					</Button>
				</div>
			</GlassContainer>
		</div>
	);
}
